import type { WorkbookDefinition, WorkbookSheet } from "@erp/shared-types";
import { reachesItself } from "./graph";
import { sheetKind } from "./sheetKinds";
import { tableItems, type WorkbookTableItem } from "./tableReferences";

export interface ExecutionItem {
  kind: "source" | "sheet";
  id: string;
  /** It cannot run: a dependency is missing, blocked, or part of a cycle. */
  blocked: boolean;
}

type Tables = Pick<WorkbookDefinition, "sources" | "sheets">;

function sheetReads(
  sheet: WorkbookSheet,
  tables: readonly WorkbookTableItem[],
): string[] {
  return sheetKind(sheet).reads(tables);
}

/**
 * The ids a Worksheet reads: its input, every Lookup source, and every table
 * its Formulas' Table references name. A Table reference that names no table
 * adds nothing: its Formula has a Diagnostic instead.
 */
export function sheetDependencies(
  sheet: WorkbookSheet,
  definition: Tables,
): string[] {
  return sheetReads(sheet, tableItems(definition));
}

/** Every Worksheet's `sheetDependencies`, by id, reading the table names once. */
export function workbookDependencies(
  definition: Tables,
): ReadonlyMap<string, readonly string[]> {
  const tables = tableItems(definition);
  return new Map(
    definition.sheets.map((sheet) => [sheet.id, sheetReads(sheet, tables)]),
  );
}

/**
 * Sources first, then each Worksheet once everything it reads is placed.
 * Worksheets that can never be placed are appended, blocked, in definition
 * order — never thrown, so editors keep showing them. Outputs compute nothing,
 * so they have no place in it.
 */
export function workbookExecutionOrder(
  definition: WorkbookDefinition,
): ExecutionItem[] {
  const result: ExecutionItem[] = definition.sources.map((source) => ({
    kind: "source",
    id: source.id,
    blocked: false,
  }));
  const placed = new Set(definition.sources.map((source) => source.id));
  const dependencies = workbookDependencies(definition);
  const pending = [...definition.sheets];
  for (;;) {
    const index = pending.findIndex((sheet) =>
      (dependencies.get(sheet.id) ?? []).every((id) => placed.has(id)),
    );
    const sheet = pending[index];
    if (!sheet) break;
    pending.splice(index, 1);
    result.push({ kind: "sheet", id: sheet.id, blocked: false });
    placed.add(sheet.id);
  }
  for (const sheet of pending)
    result.push({ kind: "sheet", id: sheet.id, blocked: true });
  return result;
}

/** Blocked Worksheets that sit on a dependency cycle (as opposed to downstream of a problem). */
export function sheetsOnCycles(
  definition: WorkbookDefinition,
  blocked: ReadonlySet<string>,
): Set<string> {
  const dependencies = workbookDependencies(definition);
  const waitsOn = new Map(
    definition.sheets
      .filter((sheet) => blocked.has(sheet.id))
      .map((sheet) => [
        sheet.id,
        (dependencies.get(sheet.id) ?? []).filter((id) => blocked.has(id)),
      ]),
  );
  return new Set(
    [...waitsOn.keys()].filter((id) =>
      reachesItself(id, (node) => waitsOn.get(node) ?? []),
    ),
  );
}
